import React from "react";
import { FlagIcon } from "@/ui";
import { UserWordStatus } from "@shared_types";

interface BaseWordCardProps {
  targetWord: string; // Spanish word
  nativeWord: string; // English word
  effectiveStatus: UserWordStatus;
  actions?: React.ReactNode; // Slot for custom action buttons
  lastWordRef?: React.Ref<HTMLDivElement>;
  /**
   * "row" = iOS WordCard layout (flag rows on the left, actions bottom-right,
   * minHeight 94, padding 16) — used by the Dictionary list.
   * "stacked" = legacy column layout (flags then actions below) — kept as the
   * default so other consumers (lesson WordList) render unchanged.
   */
  layout?: "row" | "stacked";
  /**
   * When true, wraps the non-known target word in the yellow highlight used by
   * review / lesson surfaces. Dictionary cards pass false (iOS shows plain bold
   * Spanish). Defaults to true to preserve legacy behavior for existing callers.
   */
  highlight?: boolean;
}

const BaseWordCard: React.FC<BaseWordCardProps> = ({
  targetWord,
  nativeWord,
  effectiveStatus,
  actions,
  lastWordRef,
  layout = "stacked",
  highlight = true,
}) => {
  const highlighted = highlight && effectiveStatus !== "known";

  if (layout === "row") {
    return (
      <div
        ref={lastWordRef}
        className="flex flex-row items-end justify-between bg-white rounded-[32px] p-4 gap-2 min-h-[94px] shadow-[var(--shadow-card)]"
      >
        {/* Left: stacked flag rows */}
        <div className="flex flex-col gap-2 flex-grow min-w-0">
          <div className="flex items-center w-full">
            <FlagIcon
              countryCode="ES"
              size="small"
              className="mr-2 flex-shrink-0"
            />
            <div className="font-bold break-words text-left text-base min-w-0">
              <span
                className={
                  highlighted
                    ? "bg-[var(--color-light-yellow)] rounded-md px-[3px]"
                    : ""
                }
              >
                {targetWord}
              </span>
            </div>
          </div>

          <div className="flex items-center w-full">
            <FlagIcon
              countryCode="UK"
              size="small"
              className="mr-2 flex-shrink-0"
            />
            <div className="break-words text-left text-base text-[var(--color-grey-other)] min-w-0">
              {nativeWord}
            </div>
          </div>
        </div>

        {/* Right: actions slot, bottom-aligned */}
        {actions && (
          <div className="flex flex-wrap gap-1 justify-end items-end flex-shrink-0">
            {actions}
          </div>
        )}
      </div>
    );
  }

  // Legacy stacked layout (unchanged for non-dictionary consumers).
  return (
    <div
      ref={lastWordRef}
      className="flex flex-col p-3 bg-white rounded-xl gap-3"
    >
      {/* Spanish word row */}
      <div className="flex items-center w-full">
        <FlagIcon
          countryCode="ES"
          size="small"
          className="mr-2 flex-shrink-0 hidden xs:inline-flex"
        />
        <div className="font-bold break-words text-left text-base flex-grow">
          <span
            className={
              highlighted
                ? "bg-[var(--color-light-yellow)] rounded-md px-[3px]"
                : ""
            }
          >
            {targetWord}
          </span>
        </div>
      </div>

      {/* English translation row with actions */}
      <div className="flex items-center w-full">
        <FlagIcon
          countryCode="UK"
          size="small"
          className="mr-2 flex-shrink-0 hidden xs:inline-flex"
        />
        <div className="break-words text-left text-base text-[var(--color-grey)] flex-grow">
          {nativeWord}
        </div>

        {/* Actions slot */}
        {actions && <div className="flex flex-wrap gap-1 ml-2">{actions}</div>}
      </div>
    </div>
  );
};

export default BaseWordCard;
